import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Project } from '@/constants/projects'

import Image from 'next/image'
import Link from 'next/link'
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Quote
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ButtonLink } from '@/components/generals/button'
import { Scallop } from '@/components/generals/scallop'
import { Sticker } from '@/components/generals/sticker'
import { PROJECTS, getProject } from '@/constants/projects'

type NextProject = Pick<Project, 'slug' | 'name' | 'category' | 'summary' | 'image' | 'color'>

interface CaseStudyProps {
    project: Project
    next: NextProject
}

const RESULT_COLORS = ['bg-butter', 'bg-mint', 'bg-bubblegum']

const CaseStudy: FC<CaseStudyProps> = ({ project, next }): ReactNode => {
    const details = [
        { label: 'Client', value: project.client },
        { label: 'Services', value: project.services.join(', ') },
        { label: 'Year', value: project.year },
        { label: 'Timeline', value: project.timeline }
    ]

    return (
        <Layout>
            <Seo title={project.name} description={project.summary} ogImage={project.shareImage} />

            <article>
                <header className='bg-cream pt-32 pb-14 sm:pt-40 sm:pb-16'>
                    <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                        <Link
                            href='/#work'
                            className='inline-flex items-center gap-2 rounded-full font-heading text-lg tracking-wide text-plum underline-offset-4 hover:underline'
                        >
                            <ArrowLeft className='size-5' aria-hidden='true' />
                            All work
                        </Link>

                        <p className='mt-10 font-heading text-xl tracking-wide text-coral'>
                            {project.category}
                        </p>

                        <h1 className='relative mt-2 inline-block font-heading text-[clamp(3.5rem,11vw,9rem)] leading-[0.85] text-plum animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both'>
                            {project.name}

                            <Sticker
                                label='Case study'
                                shape='burst'
                                colorClassName='text-butter'
                                className='absolute -top-8 -right-16 size-24 rotate-12 text-base motion-safe:animate-wiggle sm:-right-24 sm:size-28 sm:text-lg'
                            />
                        </h1>

                        <p className='mt-6 max-w-2xl text-xl leading-relaxed text-plum/80'>
                            {project.summary}
                        </p>

                        <dl className='mt-10 grid gap-6 rounded-[2rem] border-2 border-plum bg-card p-6 shadow-[0_5px_0_0_var(--color-plum)] sm:grid-cols-2 sm:p-8 lg:grid-cols-4'>
                            {details.map((detail) => (
                                <div key={detail.label}>
                                    <dt className='font-heading text-base tracking-wide text-plum/60'>
                                        {detail.label}
                                    </dt>

                                    <dd className='mt-1 font-medium text-plum'>
                                        {detail.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </header>

                <div className='bg-cream px-5 sm:px-8'>
                    <div className={cn('relative mx-auto aspect-[4/3] max-w-6xl overflow-hidden rounded-[2.5rem] border-2 border-plum shadow-[0_8px_0_0_var(--color-plum)] sm:aspect-[16/9]', project.color)}>
                        <Image
                            src={project.image}
                            alt={project.imageAlt}
                            fill
                            preload
                            sizes='(min-width: 1152px) 1088px, 94vw'
                            className='object-cover'
                        />
                    </div>
                </div>

                <div className='bg-cream py-20 sm:py-28'>
                    <div className='mx-auto grid max-w-6xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16'>
                        <section aria-labelledby='challenge-title'>
                            <h2 id='challenge-title' className='font-heading text-[clamp(2.5rem,5vw,3.75rem)] leading-none text-plum'>
                                The challenge
                            </h2>

                            <p className='mt-6 text-lg leading-relaxed text-plum/85'>
                                {project.challenge}
                            </p>
                        </section>

                        <section aria-labelledby='approach-title'>
                            <h2 id='approach-title' className='font-heading text-[clamp(2.5rem,5vw,3.75rem)] leading-none text-plum'>
                                What we did
                            </h2>

                            {project.approach.map((paragraph) => (
                                <p key={paragraph} className='mt-6 text-lg leading-relaxed text-plum/85'>
                                    {paragraph}
                                </p>
                            ))}

                            <h3 className='mt-9 font-heading text-2xl text-plum'>
                                What we delivered
                            </h3>

                            <ul className='mt-4 grid gap-3 sm:grid-cols-2'>
                                {project.deliverables.map((deliverable) => (
                                    <li key={deliverable} className='flex items-start gap-3 font-medium text-plum'>
                                        <span className='mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-plum text-cream'>
                                            <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                        </span>
                                        {deliverable}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>
                </div>

                <section aria-labelledby='results-title' className='on-dark relative bg-plum-deep py-20 text-cream sm:py-28'>
                    <Scallop className='text-plum-deep' />

                    <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                        <h2 id='results-title' className='font-heading text-[clamp(2.75rem,6vw,4.75rem)] leading-none'>
                            The results
                        </h2>

                        <dl className='mt-12 grid gap-5 md:grid-cols-3'>
                            {project.results.map((result, index) => (
                                <div
                                    key={result.label}
                                    className={cn('reveal flex flex-col-reverse rounded-[2rem] p-7 text-plum sm:p-8', RESULT_COLORS[index % RESULT_COLORS.length])}
                                >
                                    <dt className='mt-3 font-medium text-plum/80'>
                                        {result.label}
                                    </dt>

                                    <dd className='font-heading text-[clamp(3.5rem,6vw,4.75rem)] leading-none'>
                                        {result.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <figure className='mt-14 max-w-4xl'>
                            <Quote className='size-12 fill-coral text-coral' aria-hidden='true' />

                            <blockquote className='mt-5 font-heading text-[clamp(1.75rem,3.4vw,2.75rem)] leading-[1.15]'>
                                <p>{project.quote.text}</p>
                            </blockquote>

                            <figcaption className='mt-6 text-cream/75'>
                                <span className='font-heading text-xl tracking-wide text-butter'>
                                    {project.quote.name}
                                </span>
                                {`, ${project.quote.role}`}
                            </figcaption>
                        </figure>
                    </div>
                </section>

                <section aria-labelledby='next-title' className='relative bg-cream py-20 sm:py-28'>
                    <Scallop className='text-cream' />

                    <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                        <h2 id='next-title' className='sr-only'>
                            Next project
                        </h2>

                        <Link
                            href={`/work/${next.slug}`}
                            className='group grid items-center gap-8 rounded-[2.5rem] border-2 border-plum bg-card p-4 shadow-[0_6px_0_0_var(--color-plum)] transition-transform duration-300 hover:-translate-y-1 sm:p-5 md:grid-cols-[1fr_1.15fr]'
                        >
                            <div className={cn('relative aspect-[4/3] overflow-hidden rounded-[2rem] border-2 border-plum', next.color)}>
                                <Image
                                    src={next.image}
                                    alt=''
                                    fill
                                    sizes='(min-width: 768px) 500px, 92vw'
                                    className='object-cover transition-transform duration-700 ease-out group-hover:scale-105'
                                />
                            </div>

                            <div className='px-3 pb-4 md:pr-8 md:pb-0'>
                                <p className='font-heading text-lg tracking-wide text-coral'>
                                    Next project
                                </p>

                                <p className='mt-2 font-heading text-[clamp(2.75rem,5.5vw,4.5rem)] leading-[0.9] text-plum'>
                                    {next.name}
                                </p>

                                <p className='mt-4 max-w-md text-plum/75'>
                                    {next.summary}
                                </p>

                                <span className='mt-6 inline-flex items-center gap-2 font-heading text-lg text-plum underline decoration-2 underline-offset-4 group-hover:decoration-coral'>
                                    See the case study
                                    <ArrowRight className='size-4 transition-transform group-hover:translate-x-1' aria-hidden='true' />
                                </span>
                            </div>
                        </Link>

                        <div className='mt-20 text-center'>
                            <p className='font-heading text-[clamp(2.5rem,6vw,4.5rem)] leading-none text-plum'>
                                Want results like these?
                            </p>

                            <p className='mx-auto mt-4 max-w-md text-lg text-plum/75'>
                                Tell us where you want your brand to be in a year. We'll show you how to get there.
                            </p>

                            <ButtonLink href='/#contact' size='lg' className='mt-8'>
                                Start a project
                                <ArrowRight className='size-5' aria-hidden='true' />
                            </ButtonLink>
                        </div>
                    </div>
                </section>
            </article>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: PROJECTS.map((project) => ({ params: { slug: project.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<CaseStudyProps, { slug: string }> = async ({ params }) => {
    const project = params ? getProject(params.slug) : undefined

    if (!project) return { notFound: true }

    const index = PROJECTS.findIndex((item) => item.slug === project.slug)
    const nextProject = PROJECTS[(index + 1) % PROJECTS.length]

    return {
        props: {
            project,
            next: {
                slug: nextProject.slug,
                name: nextProject.name,
                category: nextProject.category,
                summary: nextProject.summary,
                image: nextProject.image,
                color: nextProject.color
            }
        }
    }
}

export default CaseStudy
